import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'programmer',
    emoji: '💻',
    title: '게임 개발자',
    category: '과학·IT',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `게임 개발자 지훈씨는 캐릭터 공격력을 다항식으로 설계합니다.
레벨 x일 때 기본 공격력 3x²과 아이템 보너스 5x를 더해
전체 공격력 3x² + 5x를 계산해 게임 밸런스를 맞춥니다.
"다항식 하나로 캐릭터의 모든 능력치를 표현할 수 있어!"`,
    situation: '레벨 x일 때 공격력 = 3x², 아이템 보너스 = 5x. 레벨 4일 때 전체 공격력 3x² + 5x를 구하시오.',
    question: '레벨(x) = 4일 때, 전체 공격력 3x² + 5x의 값은?',
    xLabel: '레벨 (x)',
    yLabel: '공격력 값',
    equation: '공격력 = 3x² + 5x',
    answer: '3×(4²) + 5×4 = 3×16 + 20 = 48 + 20 = 68',
    whyStat: '게임 개발자는 단항식·다항식으로 캐릭터 능력치·아이템 효과·경험치 공식을 설계해 게임 밸런스를 맞춥니다.',
    insight: '넥슨·엔씨소프트·크래프톤 등 게임 회사의 개발자는 다항식·지수 공식으로 캐릭터 스탯과 게임 밸런스를 설계합니다.',
    realAnswer: 68,
    unit: '(공격력)',
    tableData: [
      { x: 'x = 1', y: 8 },
      { x: 'x = 2', y: 22 },
      { x: 'x = 3', y: 42 },
      { x: 'x = 4', y: 68 },
      { x: 'x = 5', y: 100 },
    ],
    quiz: [
      { q: '3x² + 5x에서 최고차항의 계수와 차수는?', a: '최고차항의 계수: 3, 차수: 2 (이차다항식)', hint: '최고차항 = 차수가 가장 높은 항. 3x²의 계수=3, 차수=2' },
      { q: 'x = 2일 때, 3x² + 5x의 값은?', a: '3×4 + 5×2 = 12 + 10 = 22', hint: 'x=2 대입: 3×(2²)+5×2 = 3×4+10 = 22' },
    ],
    jobs: ['게임 개발자', '앱 개발자', '데이터 엔지니어', '알고리즘 설계사'],
  },
  {
    id: 'nutritionist',
    emoji: '🥗',
    title: '영양사',
    category: '의료·식품',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `병원 영양사 수아씨는 환자별 하루 칼로리를 단항식으로 계산합니다.
체중 x(kg)일 때 기초대사량 = 25x(kcal),
활동 칼로리 = 10x(kcal)를 더해 총 필요 칼로리를 구합니다.
"단항식 덧셈으로 각 영양소 섭취량을 정확히 계산해!"`,
    situation: '체중 x(kg)일 때, 기초대사량 25x kcal + 활동 칼로리 10x kcal. 체중 60kg인 환자의 하루 필요 칼로리는?',
    question: 'x = 60일 때, 25x + 10x를 동류항끼리 합산한 값은?',
    xLabel: '체중 x (kg)',
    yLabel: '칼로리 (kcal)',
    equation: '칼로리 = 25x + 10x = 35x',
    answer: '25x + 10x = (25+10)x = 35x\nx = 60 대입: 35 × 60 = 2100 kcal',
    whyStat: '영양사는 단항식의 동류항 합산으로 환자별 영양소 필요량을 정확히 계산해 식단을 설계합니다.',
    insight: '병원·스포츠 팀·급식 기업의 영양사는 체중·활동량을 변수로 하는 단항식 공식으로 개인 맞춤 식단을 만듭니다.',
    realAnswer: 2100,
    unit: 'kcal',
    tableData: [
      { x: 'x = 40', y: 1400 },
      { x: 'x = 50', y: 1750 },
      { x: 'x = 60', y: 2100 },
      { x: 'x = 70', y: 2450 },
      { x: 'x = 80', y: 2800 },
    ],
    quiz: [
      { q: '25x + 10x를 동류항끼리 정리하면?', a: '35x (계수끼리 합산: 25+10=35)', hint: '동류항: 문자와 차수가 같은 항. 25x와 10x는 모두 x의 1차항' },
      { q: '체중 50kg일 때 하루 필요 칼로리(35x)는?', a: '35 × 50 = 1750 kcal', hint: 'x=50 대입: 35×50=1750' },
    ],
    jobs: ['영양사', '식품 연구원', '스포츠 영양 전문가', '급식 관리사'],
  },
  {
    id: 'fashion',
    emoji: '👗',
    title: '패션 디자이너',
    category: '예술·디자인',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `패션 디자이너 예은씨는 옷감 필요량을 다항식으로 계산합니다.
허리 둘레 x(cm)인 옷을 만들 때
앞면 2x + 10, 뒷면 2x + 6 cm의 원단이 필요합니다.
"다항식을 합산해 원단 발주량을 정확히 결정해!"`,
    situation: '앞면 원단 = (2x + 10) cm, 뒷면 원단 = (2x + 6) cm. 총 원단 길이를 x로 표현하시오.',
    question: '(2x + 10) + (2x + 6)을 동류항끼리 정리하면?',
    xLabel: '항목',
    yLabel: '원단 길이 (cm)',
    equation: '총 원단 = (2x+10) + (2x+6) = 4x + 16',
    answer: '(2x + 10) + (2x + 6)\n= (2x + 2x) + (10 + 6)\n= 4x + 16',
    whyStat: '패션 디자이너는 다항식 계산으로 치수별 원단 발주량을 결정하고 생산 비용을 최소화합니다.',
    insight: '삼성물산·LF·무신사 등 패션 기업의 MD와 디자이너는 치수·원단량을 다항식으로 계산해 효율적으로 생산 계획을 세웁니다.',
    realAnswer: 4,
    unit: '(x의 계수)',
    tableData: [
      { x: '앞면', y: '2x + 10' as unknown as number },
      { x: '뒷면', y: '2x + 6' as unknown as number },
      { x: '소매', y: 'x + 4' as unknown as number },
      { x: '칼라', y: 'x + 2' as unknown as number },
      { x: '합계', y: '6x + 22' as unknown as number },
    ],
    quiz: [
      { q: '(3x + 5) + (x + 7)을 간단히 하면?', a: '4x + 12', hint: '동류항끼리: 3x+x=4x, 5+7=12' },
      { q: '(5x + 8) - (2x + 3)을 간단히 하면?', a: '3x + 5', hint: '빼는 식의 부호를 바꿔서: 5x+8-2x-3 = 3x+5' },
    ],
    jobs: ['패션 디자이너', '의류 MD', '원단 소싱 전문가', '패턴사'],
  },
  {
    id: 'chemist',
    emoji: '🧪',
    title: '화학 연구원',
    category: '과학·연구',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `화학 연구원 민준씨는 반응 속도를 단항식으로 표현합니다.
농도 x(mol/L)일 때 반응 속도 = 3x × 2x = 6x² (mol²/L²·s).
단항식의 곱셈을 이용해 최적 반응 조건을 찾습니다.
"지수법칙으로 복잡한 화학 공식도 간결하게 표현해!"`,
    situation: '반응 속도 = 3x × 2x. 단항식의 곱셈으로 정리하고, x = 2일 때 반응 속도를 구하시오.',
    question: '3x × 2x를 간단히 하고, x = 2를 대입한 값은?',
    xLabel: '농도 x (mol/L)',
    yLabel: '반응 속도',
    equation: '반응 속도 = 3x × 2x = 6x²',
    answer: '3x × 2x = (3×2) × (x×x) = 6x²\nx = 2 대입: 6 × (2²) = 6 × 4 = 24',
    whyStat: '화학 연구원은 단항식의 곱셈과 지수법칙으로 반응 속도·농도·온도 공식을 간결하게 표현하고 실험을 설계합니다.',
    insight: 'LG화학·삼성SDI·한화 등 화학·제약 기업의 연구원은 단항식 연산으로 물질 반응 공식을 도출해 신소재를 개발합니다.',
    realAnswer: 24,
    unit: '(반응 속도)',
    tableData: [
      { x: 'x = 1', y: 6 },
      { x: 'x = 2', y: 24 },
      { x: 'x = 3', y: 54 },
      { x: 'x = 4', y: 96 },
      { x: 'x = 5', y: 150 },
    ],
    quiz: [
      { q: '4a² × 3a³를 간단히 하면?', a: '12a⁵', hint: '계수끼리 곱: 4×3=12. 지수법칙: a²×a³=a^(2+3)=a⁵' },
      { q: '지수법칙 aᵐ × aⁿ = ?', a: 'a^(m+n) (지수끼리 더함)', hint: '같은 밑의 곱셈은 지수를 더합니다' },
    ],
    jobs: ['화학 연구원', '신소재 개발자', '제약 연구원', '환경 공학자'],
  },
  {
    id: 'architect',
    emoji: '🏗️',
    title: '건축 설계사',
    category: '건축·공학',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `건축 설계사 지우씨는 건물 넓이를 다항식 곱셈으로 계산합니다.
가로 (x + 3)m, 세로 (x + 2)m인 방의 넓이를
분배법칙으로 전개해 정확한 면적을 구합니다.
"(x+3)(x+2)를 전개하면 실제 필요한 자재량을 알 수 있어!"`,
    situation: '방의 가로 = (x + 3) m, 세로 = (x + 2) m. 분배법칙을 이용해 넓이를 x에 대한 다항식으로 나타내시오.',
    question: '(x + 3)(x + 2)를 전개하면?',
    xLabel: '항목',
    yLabel: '넓이 (m²)',
    equation: '넓이 = (x+3)(x+2) = x² + 5x + 6',
    answer: '(x + 3)(x + 2)\n= x(x+2) + 3(x+2)\n= x² + 2x + 3x + 6\n= x² + 5x + 6',
    whyStat: '건축 설계사는 다항식 곱셈과 분배법칙으로 건물 면적·자재량을 계산해 효율적인 건물을 설계합니다.',
    insight: '삼성물산건설·현대건설·DL이앤씨 등 건설사의 설계사는 다항식 전개로 공간 설계와 건축 자재량을 산출합니다.',
    realAnswer: 6,
    unit: '(상수항)',
    tableData: [
      { x: 'x = 1', y: 12 },
      { x: 'x = 2', y: 20 },
      { x: 'x = 3', y: 30 },
      { x: 'x = 4', y: 42 },
      { x: 'x = 5', y: 56 },
    ],
    quiz: [
      { q: '(x + 4)(x + 1)을 전개하면?', a: 'x² + 5x + 4', hint: 'x(x+1)+4(x+1) = x²+x+4x+4 = x²+5x+4' },
      { q: '(a + b)(c + d) 전개 시 항의 개수는?', a: '4개 (ac, ad, bc, bd)', hint: '분배법칙: 앞 괄호 2항 × 뒤 괄호 2항 = 4항' },
    ],
    jobs: ['건축 설계사', '인테리어 디자이너', '토목 공학자', '도시 계획가'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '💻',
    title: '게임·소프트웨어 개발자',
    example: '능력치·아이템 효과를 다항식으로 설계해 게임 밸런스 최적화',
    formula: '공격력 = ax² + bx + c (다차 다항식)',
    salary: '연 4,500만~1억 2,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🏗️',
    title: '건축·토목 설계사',
    example: '공간 넓이·자재량을 다항식 곱셈으로 계산해 건물 설계',
    formula: '넓이 = (a+b)(c+d) = ac+ad+bc+bd',
    salary: '연 4,000만~9,000만원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '🧪',
    title: '화학·제약 연구원',
    example: '반응 속도·농도를 단항식 곱셈·지수법칙으로 표현',
    formula: 'aᵐ × aⁿ = a^(m+n) (지수법칙)',
    salary: '연 4,500만~1억원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🥗',
    title: '영양사·식품 연구원',
    example: '체중·활동량 변수로 칼로리 단항식 공식 설계',
    formula: '칼로리 = ax + bx = (a+b)x (동류항 합산)',
    salary: '연 3,500만~7,000만원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '👗',
    title: '패션·의류 전문가',
    example: '치수별 원단량을 다항식 덧셈으로 계산해 생산 계획 수립',
    formula: '총 원단 = (ax+b) + (cx+d) = (a+c)x+(b+d)',
    salary: '연 3,500만~8,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '📐',
    title: '수학·과학 교육자',
    example: '단항식·다항식 개념으로 학생들의 수학적 사고력 향상',
    formula: '다항식 = 단항식들의 합 (동류항 정리)',
    salary: '연 4,000만~8,500만원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '📋',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '다음 다항식에서 동류항끼리 모아 간단히 하시오.\n3x² + 2x + 5x² - x + 4',
    hint: '동류항: x²끼리, x끼리, 상수끼리 묶어서 계산',
    answer: '8x² + x + 4',
    explanation: '3x² + 5x² = 8x²\n2x - x = x\n상수 = 4\n따라서 8x² + x + 4',
  },
  {
    id: 'c2',
    emoji: '🌿',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '단항식의 곱셈을 이용해 계산하시오.\n(-2x²y) × 3xy² = ?',
    hint: '계수끼리 곱하고, 같은 문자의 지수는 더한다 (지수법칙)',
    answer: '-6x³y³',
    explanation: '계수: (-2) × 3 = -6\nx끼리: x² × x = x^(2+1) = x³\ny끼리: y × y² = y^(1+2) = y³\n→ -6x³y³',
  },
  {
    id: 'c3',
    emoji: '📈',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '분배법칙을 이용해 전개하시오.\n2x(3x - 4) + x(x + 5) = ?',
    hint: '각 괄호를 먼저 분배법칙으로 전개한 후 동류항을 정리',
    answer: '7x² - 3x',
    explanation: '2x(3x-4) = 6x² - 8x\nx(x+5) = x² + 5x\n합산: 6x² + x² = 7x²\n-8x + 5x = -3x\n→ 7x² - 3x',
  },
  {
    id: 'c4',
    emoji: '🔬',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '곱셈 공식을 이용해 전개하시오.\n(x + 3)² - (x - 2)(x + 2) = ?',
    hint: '(a+b)² = a²+2ab+b², (a+b)(a-b) = a²-b². 각각 전개 후 빼기!',
    answer: '6x + 5',
    explanation: '(x+3)² = x² + 6x + 9\n(x-2)(x+2) = x² - 4\n→ (x²+6x+9) - (x²-4)\n= x² + 6x + 9 - x² + 4\n= 6x + 13\n\n※ 정확한 계산: 6x + 13',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '과학·IT': 'bg-violet-100 text-violet-700',
  '의료·식품': 'bg-emerald-100 text-emerald-700',
  '예술·디자인': 'bg-rose-100 text-rose-700',
  '과학·연구': 'bg-amber-100 text-amber-700',
  '건축·공학': 'bg-sky-100 text-sky-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'mono' | 'poly'

interface CalcResult {
  mode: CalcMode
  steps: string[]
  outputVal: number | string
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('mono')
  const [coef, setCoef] = useState('')
  const [xVal, setXVal] = useState('')
  const [exp, setExp] = useState('2')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setCoef(''); setXVal(''); setExp('2'); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const c = parseFloat(coef)
    const x = parseFloat(xVal)
    const e = parseInt(exp)
    if (isNaN(c) || isNaN(x)) return

    if (mode === 'mono') {
      const val = c * Math.pow(x, isNaN(e) ? 1 : e)
      setResult({
        mode,
        outputVal: val,
        steps: [
          `공식: 단항식 = ${c}x${isNaN(e) ? '' : `^${e}`}`,
          `x = ${x} 대입`,
          `= ${c} × ${x}^${isNaN(e) ? 1 : e}`,
          `= ${c} × ${Math.pow(x, isNaN(e) ? 1 : e)}`,
          `= ${val}`,
        ],
      })
    } else {
      // poly: ax² + bx (coef = a, exp = b 로 사용)
      const a = c
      const b = parseFloat(exp)
      if (isNaN(b)) return
      const val = a * x * x + b * x
      setResult({
        mode,
        outputVal: val,
        steps: [
          `공식: 다항식 = ${a}x² + ${b}x`,
          `x = ${x} 대입`,
          `= ${a} × (${x})² + ${b} × ${x}`,
          `= ${a} × ${x * x} + ${b * x}`,
          `= ${a * x * x} + ${b * x}`,
          `= ${val}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const outVal = typeof result.outputVal === 'number' ? result.outputVal : parseFloat(String(result.outputVal))
    const tolerance = Math.abs(outVal) * 0.02 + 0.001
    setChecked(Math.abs(ua - outVal) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('mono')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'mono' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            단항식
          </button>
          <button
            onClick={() => handleModeChange('poly')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'poly' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            다항식
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-violet-700 text-sm">
          {mode === 'mono'
            ? '단항식 값 = 계수 × x^지수'
            : '다항식 값 = ax² + bx (x 대입)'}
        </span>
      </div>

      {mode === 'mono' ? (
        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 계수 (a)</label>
            <input
              type="number"
              value={coef}
              onChange={e => { setCoef(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 3"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 지수</label>
            <input
              type="number"
              value={exp}
              onChange={e => { setExp(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 2"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 x 값</label>
            <input
              type="number"
              value={xVal}
              onChange={e => { setXVal(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 4"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={calculate}
              disabled={!coef || !xVal}
              className="px-5 py-2.5 rounded-xl font-bold text-sm transition-all bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
            >
              계산!
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 a (x²의 계수)</label>
            <input
              type="number"
              value={coef}
              onChange={e => { setCoef(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 3"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 b (x의 계수)</label>
            <input
              type="number"
              value={exp}
              onChange={e => { setExp(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 5"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-500">🔢 x 값</label>
            <input
              type="number"
              value={xVal}
              onChange={e => { setXVal(e.target.value); setResult(null); setChecked(null) }}
              onKeyDown={e => e.key === 'Enter' && calculate()}
              placeholder="예: 4"
              className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={calculate}
              disabled={!coef || !xVal || !exp}
              className="px-5 py-2.5 rounded-xl font-bold text-sm transition-all bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
            >
              계산!
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'mono' ? 'bg-violet-50 border-violet-300' : 'bg-indigo-50 border-indigo-300'
          }`}>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'mono' ? 'text-violet-700' : 'text-indigo-700'}`}>
                {result.outputVal}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="내 답 입력"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3 whitespace-pre-line">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function A2_1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 대수', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 단항식·다항식</h1>
            <p className="text-sm text-slate-500">게임 개발자, 건축가, 화학 연구원, 영양사, 패션 디자이너 — 모두 단항식·다항식을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 단항식·다항식을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">지수법칙·단항식 곱셈·다항식 덧셈·분배법칙이 세상 모든 수식 설계의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '💻', job: '게임 개발자' },
              { icon: '🥗', job: '영양사' },
              { icon: '👗', job: '패션 디자이너' },
              { icon: '🧪', job: '화학 연구원' },
              { icon: '🏗️', job: '건축 설계사' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 단항식·다항식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 다항식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-base font-black text-violet-700 font-mono">{selected.equation}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 대수인가?</span> {selected.whyStat}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" /> 값 변화 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">{selected.yLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">{row.y}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값 변화표를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 대수를 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['지수법칙', '단항식 곱셈', '다항식 덧셈', '동류항 정리', '분배법칙', '곱셈 공식'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 단항식·다항식 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔢', title: '지수법칙', desc: 'aᵐ × aⁿ = a^(m+n)\naᵐ ÷ aⁿ = a^(m-n)\n(aᵐ)ⁿ = a^(mn)\n(ab)ⁿ = aⁿbⁿ', color: 'text-violet-600' },
                { icon: '✖️', title: '단항식의 곱셈·나눗셈', desc: '계수끼리 곱/나누고\n문자끼리는 지수법칙 적용\n3x² × 2x = 6x³\n6x³ ÷ 2x = 3x²', color: 'text-indigo-600' },
                { icon: '➕', title: '다항식 덧셈·분배법칙', desc: '동류항: 문자·차수 모두 같은 항\n동류항끼리만 계수 합산 가능\na(b+c) = ab+ac (분배법칙)\n(a+b)(c+d) = ac+ad+bc+bd', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 단항식·다항식을 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              지수법칙·단항식·다항식·분배법칙은 IT·건축·화학·의료·패션 등 수많은 직업의 핵심 계산 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 대수가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: 'AI·머신러닝 모델 설계', desc: '신경망의 가중치 계산, 활성화 함수 설계 등 모든 AI 알고리즘이 다항식·행렬 연산으로 구성됩니다.', color: 'border-yellow-400' },
                { title: '게임·시뮬레이션 엔진', desc: '물리 엔진의 충돌 감지, 캐릭터 이동 경로 계산에 고차 다항식이 활용됩니다.', color: 'border-violet-400' },
                { title: '암호화·보안 기술', desc: 'RSA·AES 암호화는 큰 수의 지수법칙과 다항식 연산을 기반으로 데이터를 보호합니다.', color: 'border-emerald-400' },
                { title: '3D 그래픽·메타버스', desc: '3D 공간의 좌표 변환, 카메라 시점 계산에 다항식·행렬 곱셈이 핵심적으로 사용됩니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 대수에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '문자와 식', active: false },
                { step: '중2', topic: '단항식·다항식', active: true },
                { step: '중3', topic: '인수분해', active: false },
                { step: '고1', topic: '다항식 연산', active: false },
                { step: '고2', topic: '방정식·부등식', active: false },
                { step: '대학', topic: '선형대수', active: false },
                { step: '직업', topic: 'AI·데이터 과학', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 단항식·다항식의 계산을 직접 해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 단항식·다항식 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 지수법칙', desc: 'aᵐ × aⁿ = a^(m+n)\naᵐ ÷ aⁿ = a^(m-n)\n(aᵐ)ⁿ = a^(mn)\n(ab)ⁿ = aⁿbⁿ', example: '예: x² × x³ = x⁵', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② 단항식 연산', desc: '곱셈: 계수 곱 × 지수 합\n나눗셈: 계수 나누고 지수 빼기\n동류항: 문자·차수가 같은 항\n동류항만 계수끼리 더하기 가능', example: '예: 3x² × 2x = 6x³', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ 다항식 전개', desc: '분배법칙: a(b+c) = ab+ac\n(a+b)(c+d) 전개: 4항\n곱셈공식: (a+b)²=a²+2ab+b²\n(a+b)(a-b) = a²-b²', example: '예: (x+2)(x+3) = x²+5x+6', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">지수법칙 → 단항식 계산 → 동류항 정리 → 분배법칙 → 곱셈 공식!</p>
              <p className="text-xs text-violet-500 mt-1">대수의 흐름을 이해하면 어떤 수식도 간결하게 표현할 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
